// src/screens/admin/vehiclerate/VehicleRates.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { Row, Col } from "react-bootstrap";
import { FaPlus, FaTimes, FaTruck, FaWeightHanging } from "react-icons/fa";
import Button from "../../../components/general/button/Button.js";
import StatCard from "../../../components/admin/statcard/StatCard.js";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown.js";
import "../../../assets/css/admin/vehicles/Vehicles.css";
import AdminLayout from "../../../layouts/AdminLayout.js";
import MyContext from "../../../context/MyContext.js";

const Vehicles = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(MyContext);

  const [vehicleRatesList, setVehicleRatesList] = useState([]);
  const [vehicleRateStats, setVehicleRateStats] = useState({
    total_vehicle_types: 0,
    max_capacity_kg: 0,
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Form fields
  const [vehicleType, setVehicleType] = useState("");
  const [minCapacityKg, setMinCapacityKg] = useState("");
  const [maxCapacityKg, setMaxCapacityKg] = useState("");
  const [idToUpdate, setIdToUpdate] = useState("");

  ////////// Vehicle Rate APIs

  const viewAllVehicleRates = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/vehicle/viewAllVehicles`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setVehicleRatesList(response.data.vehicles);
          setVehicleRateStats(response.data.stats);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load vehicle rates.",
        });
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllVehicleRates();
  }, [authToken]);

  const resetForm = () => {
    setVehicleType("");
    setMinCapacityKg("");
    setMaxCapacityKg("");
    setIdToUpdate("");
  };

  const hasNegativeValue = () => {
    return Number(minCapacityKg) < 0 || Number(maxCapacityKg) < 0;
  };

  const addVehicleRate = () => {
    if (hasNegativeValue()) {
      Swal.fire({
        icon: "error",
        title: "Invalid Value",
        text: "Capacity cannot be negative.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("vehicle_type", vehicleType);
    formData.append("min_capacity_kg", minCapacityKg);
    formData.append("max_capacity_kg", maxCapacityKg);

    setSubmitting(true);
    axios
      .post(`${baseUrl}/admin/vehicle/addVehicle`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setSubmitting(false);
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Vehicle type added successfully!",
          });
          setShowAddModal(false);
          resetForm();
          viewAllVehicleRates();
        }
      })
      .catch((error) => {
        setSubmitting(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to add vehicle type.",
        });
      });
  };

  const updateVehicleRate = () => {
    if (hasNegativeValue()) {
      Swal.fire({
        icon: "error",
        title: "Invalid Value",
        text: "Capacity cannot be negative.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("id", idToUpdate);
    formData.append("vehicle_type", vehicleType);
    formData.append("min_capacity_kg", minCapacityKg);
    formData.append("max_capacity_kg", maxCapacityKg);

    setSubmitting(true);
    axios
      .post(`${baseUrl}/admin/vehicle/updateVehicle`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setSubmitting(false);
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Vehicle updated successfully!",
          });
          setShowAddModal(false);
          resetForm();
          viewAllVehicleRates();
        }
      })
      .catch((error) => {
        setSubmitting(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update vehicle.",
        });
      });
  };

  const deleteVehicleRate = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This vehicle type will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setDeletingId(id);
        const formData = new FormData();
        formData.append("id", id);
        axios
          .post(`${baseUrl}/admin/vehicle/deleteVehicle`, formData, {
            headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            setDeletingId(null);
            if (response.data.hasOwnProperty("error")) {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: response.data.error,
              });
            } else {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Vehicle type deleted successfully!",
              });
              viewAllVehicleRates();
            }
          })
          .catch((error) => {
            setDeletingId(null);
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete vehicle type.",
            });
          });
      }
    });
  };

  // ─── Stats ────────────────────────────────────────────────────────────────

  const statsData = [
    {
      icon: FaTruck,
      iconBg: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      label: "Vehicle Types",
      value: vehicleRateStats.total_vehicle_types.toString(),
      badge: "Active",
    },
    {
      icon: FaWeightHanging,
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      label: "Max Capacity Covered",
      value: `${vehicleRateStats.max_capacity_kg || 0} kg`,
      badge: "Live",
    },
  ];

  // ─── Actions ──────────────────────────────────────────────────────────────

  const getActions = (rate) => [
    {
      icon: "MdEdit",
      label: "Edit",
      onClick: () => {
        setIsEditing(true);
        setIdToUpdate(rate.id);
        setVehicleType(rate.vehicle_type);
        setMinCapacityKg(rate.min_capacity_kg);
        setMaxCapacityKg(rate.max_capacity_kg);
        setShowAddModal(true);
      },
    },
    {
      icon: "MdDelete",
      label: deletingId === rate.id ? "Deleting..." : "Delete",
      onClick: () => {
        if (deletingId !== null) return;
        deleteVehicleRate(rate.id);
      },
      className: "danger",
    },
  ];

  const formatVehicleTypeLabel = (type) => {
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // ── Skeleton rows — table load hone tak dikhte hain ──
  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, i) => (
      <tr key={`skeleton-${i}`}>
        {Array.from({ length: 4 }).map((__, j) => (
          <td key={j} style={{ padding: "14px 12px" }}>
            <div
              style={{
                height: 14,
                borderRadius: 6,
                background: "linear-gradient(90deg, #eef0f2 25%, #e2e5e9 37%, #eef0f2 63%)",
                backgroundSize: "400% 100%",
                animation: "ksa-vehiclerate-shimmer 1.4s ease infinite",
              }}
            />
          </td>
        ))}
      </tr>
    ));

  return (
    <AdminLayout>
      <div>
        <style>{`
          @keyframes ksa-vehiclerate-shimmer {
            0% { background-position: 100% 50%; }
            100% { background-position: 0 50%; }
          }
        `}</style>
        {/* Page Header */}
        <div className="ksa-vehiclerate-page-header">
          <div>
            <h1 className="ksa-layout-page-title">Vehicles</h1>
            <p className="ksa-layout-page-subtitle">
              Define vehicle types and their delivery capacity range. Drivers
              select from this list and cannot set their own capacity.
            </p>
          </div>
          <Button
            variant="primary"
            size="medium"
            icon={<FaPlus />}
            iconPosition="left"
            disabled={loading}
            onClick={() => {
              setIsEditing(false);
              resetForm();
              setShowAddModal(true);
            }}
          >
            Add Vehicle Type
          </Button>
        </div>

        {/* Stats Cards */}
        <Row className="g-4 mb-4 ksa-vehiclerate-stats">
          {statsData.map((stat, idx) => (
            <Col key={idx} xs={12} md={6}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>

        {/* Main Table Card */}
        <div className="ksa-card">
          <div className="ksa-vehiclerate-table-header">
            <p className="ksa-vehiclerate-table-note">
              This capacity range applies to all drivers of a vehicle type
              automatically — no per-driver overrides.
            </p>
          </div>

          <div className="ksa-vehiclerate-table-wrapper">
          <table className="ksa-vehiclerate-table">
            <thead>
              <tr>
                <th>VEHICLE TYPE</th>
                <th>MIN CAPACITY</th>
                <th>MAX CAPACITY</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                renderSkeletonRows()
              ) : (
                <>
                  {vehicleRatesList.map((rate) => (
                    <tr key={rate.id}>
                      <td className="ksa-vehiclerate-type">
                        {formatVehicleTypeLabel(rate.vehicle_type)}
                      </td>
                      <td>{rate.min_capacity_kg} kg</td>
                      <td>{rate.max_capacity_kg} kg</td>
                      <td>
                        <ActionDropdown actions={getActions(rate)} />
                      </td>
                    </tr>
                  ))}
                  {vehicleRatesList.length === 0 && (
                    <tr>
                      <td colSpan={4} className="ksa-vehiclerate-empty">
                        No vehicle types configured yet. Click "Add Vehicle Type" to
                        create one.
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setShowAddModal(false)}
          >
            <div
              className="ksa-modal ksa-modal-md ksa-vehiclerate-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-vehiclerate-modal-header">
                <h3 className="ksa-vehiclerate-modal-title">
                  {isEditing ? "Edit Vehicle" : "Add New Vehicle Type"}
                </h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => setShowAddModal(false)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-vehiclerate-modal-body">
                <div className="ksa-vehiclerate-form-group">
                  <label>Vehicle Type *</label>
                  <input
                    type="text"
                    className="ksa-vehiclerate-input"
                    placeholder="e.g., mini_loader"
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    disabled={isEditing}
                  />
                  <small>Use lowercase with underscores, e.g. medium_truck</small>
                </div>

                <Row className="g-3">
                  <Col xs={6}>
                    <div className="ksa-vehiclerate-form-group">
                      <label>Min Capacity (kg) *</label>
                      <input
                        type="number"
                        min="0"
                        className="ksa-vehiclerate-input"
                        value={minCapacityKg}
                        onChange={(e) => setMinCapacityKg(e.target.value)}
                      />
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="ksa-vehiclerate-form-group">
                      <label>Max Capacity (kg) *</label>
                      <input
                        type="number"
                        min="0"
                        className="ksa-vehiclerate-input"
                        value={maxCapacityKg}
                        onChange={(e) => setMaxCapacityKg(e.target.value)}
                      />
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="ksa-vehiclerate-modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowAddModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={submitting}
                  onClick={() => (isEditing ? updateVehicleRate() : addVehicleRate())}
                >
                  {submitting
                    ? (isEditing ? "Updating..." : "Creating...")
                    : (isEditing ? "Update Vehicle" : "Create Vehicle Type")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Vehicles;
